import type { AbilityService } from '@efdigital/ll-addon-auth';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import { buildUpdateResourceLink } from '@efdigital/ll-admin-v2/data/builders/lti/lti-resource-links';
import { deepRollback } from '@efdigital/ll-admin-v2/data/deep-rollback';
import type { LtiResourceLink } from '@efdigital/ll-admin-v2/data/types/lti';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

export class EditLtiResourceLink extends Component<
  EditRecordSignature<
    LtiResourceLink,
    {
      initialState?: 'readonly' | 'edit';
    }
  >
> {
  @service declare store: Store;
  @service declare ability: AbilityService;

  get canEditLink(): boolean {
    return this.ability.canEdit('lti-links');
  }

  setPlatformResourceLinkId = (event: Event) => {
    this.args.item.platformResourceLinkId = (
      event.target as HTMLInputElement
    ).value;
  };

  toggleIsActive = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.isActive = target.checked;
  };

  saveResourceLink = () => {
    const link = this.args.item;
    const platformId = link.platformId;
    const consumedToolId = link.consumedToolId;
    const deploymentId = link.consumedToolDeploymentId;

    if (!platformId || !consumedToolId || !deploymentId) {
      throw new Error('Resource link is missing deployment context');
    }

    return this.store.request(
      buildUpdateResourceLink(link, platformId, consumedToolId, deploymentId)
    );
  };

  revertResourceLink = () => {
    deepRollback(this.args.item, this.store);
  };

  <template>
    <EditRecord
      @editLabel="Edit resource link"
      @item={{@item}}
      @context={{@context}}
      @canEdit={{this.canEditLink}}
      @maxDepth={{0}}
      @buttonSize="btn-md"
      @save={{this.saveResourceLink}}
      @revert={{this.revertResourceLink}}
      @initialState={{if @initialState @initialState "readonly"}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <fieldset disabled={{isReadOnly}} class="mb-0">
          <legend class="gud-eyebrow">Resource link</legend>

          <div class="row">
            <div class="col-lg-6">
              <div class="mb-3">
                <label
                  class="form-label"
                  for="{{uniqueId}}-platform-resource-link-id"
                >Platform resource link ID</label>
                <input
                  id="{{uniqueId}}-platform-resource-link-id"
                  type="text"
                  class="form-control font-monospace"
                  required
                  value={{@item.platformResourceLinkId}}
                  data-test-lti-resource-link-id
                  {{on "input" this.setPlatformResourceLinkId}}
                />
                <div class="form-text">
                  The claim value from the LMS launch JWT that identifies this
                  cohort link.
                </div>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="mb-3">
                <span class="form-label d-block">Deployment</span>
                {{#if @item.platformDeploymentId}}
                  <span
                    class="font-monospace d-block"
                    data-test-lti-resource-link-deployment
                  >{{@item.platformDeploymentId}}</span>
                {{else}}
                  <span
                    class="font-monospace d-block text-muted"
                    data-test-lti-resource-link-deployment
                  >{{@item.consumedToolDeploymentId}}</span>
                {{/if}}
                <div class="form-text">
                  The LMS deployment this link belongs to. Set when the link was
                  created and cannot be changed here.
                </div>
              </div>
            </div>
          </div>

          <div class="form-check form-switch mb-0">
            <input
              type="checkbox"
              class="form-check-input"
              id="{{uniqueId}}-is-active"
              checked={{@item.isActive}}
              aria-checked={{@item.isActive}}
              role="switch"
              data-test-lti-resource-link-is-active
              {{on "change" this.toggleIsActive}}
            />
            <label class="form-check-label" for="{{uniqueId}}-is-active">
              Active
            </label>
            <div class="form-text">
              Inactive links are ignored during LTI launch enrolment.
            </div>
          </div>
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}

export default EditLtiResourceLink;
